import { Button, DateBox, NumberBox, TextBox } from "devextreme-react";
import { Button as TextBoxButton } from "devextreme-react/text-box";
import { DataItem, HCostData } from "@constants/inbound";
import { useAtom } from "jotai";
import { hcostInboundAtom } from "@/store/hcost-atom";
import PostCode from "@components/common/post-code";
import Radio from "@components/common/radio";
import useCounselHook from "@feature/counsel/useCounselHook";
import { useState } from "react";
import { ClickEvent } from "devextreme/ui/button";

interface postCode {
  fullAddress: string;
  visible: boolean;
}

const CounselConstructionInfo = () => {
  const [hCostResult, setHCostResult] = useAtom(hcostInboundAtom);
  const [postCodeVisible, setPostCodeVisible] = useState(false);
  const { onValueChangeCounselHandler, handleRadioChecked } = useCounselHook();

  const getPostCodeData = ({ fullAddress, visible }: postCode) => {
    const data = {
      ...hCostResult,
      conAddress: fullAddress,
    };
    setHCostResult(data);
    setPostCodeVisible(visible);
  };

  /** 최대 예산 버튼 클릭 */
  const conBudgetClick = (e: ClickEvent, budget: number) => {
    setHCostResult((draft: HCostInbound) => {
      draft.conBudget += budget * 10000;
    });
  };

  const addressTextBoxButtonOption = {
    icon: "search",
    onClick: () => {
      setPostCodeVisible(true);
    },
  };

  return (
    <>
      {/*시공 정보*/}
      <div className="content-fieldset">
        <h3 className="content-fieldset-title">시공 정보</h3>
        <div className="content-fieldset-title-desc" />

        <div className="content-field row">
          <div className="content-field-label">
            <div className="content-label-name">시공 예정일</div>
            <div className="content-label-desc" />
          </div>
          <div className="content-field-value full">
            <DateBox
              calendarOptions={{ maxZoomLevel: "year" }}
              showClearButton={true}
              value={hCostResult.conDate}
              displayFormat="yyyy년 MM월"
              placeholder="시공 예정월을 선택하세요."
              dateSerializationFormat="yyyy-MM-dd"
              onValueChanged={onValueChangeCounselHandler((draft, value) => {
                draft.conDate = value;
              })}
            />
            <div className="content-value-desc" />
          </div>
        </div>

        <div className="content-field">
          <div className="content-field-label">
            <div className="content-label-name">시공지 주소</div>
            <div className="content-label-desc" />
          </div>
          <div className="content-field-value full flex flex-col">
            <div>
              <PostCode
                isOpen={postCodeVisible}
                onClose={(e: boolean) => setPostCodeVisible(e)}
                getPostcodeData={(e: postCode) => getPostCodeData(e)}
              />
            </div>

            <div className="grid sm:grid-cols-12 gap-4 py-1 ">
              <div className="col-span-2 sm:text-left md:text-right">
                <div className="content-label-name">주소</div>
                <div className="content-label-desc" />
              </div>
              <div className="col-span-10">
                <div className="grow pr-3">
                  <TextBox
                    value={hCostResult.conAddress}
                    onValueChanged={onValueChangeCounselHandler((draft, value) => {
                      draft.conAddress = value;
                    })}
                  >
                    <TextBoxButton location="after" name="search" options={addressTextBoxButtonOption}></TextBoxButton>
                  </TextBox>
                </div>
                <div className="content-value-desc" />
              </div>
            </div>

            <div className="grid sm:grid-cols-12 gap-4 py-1">
              <div className="col-span-2 sm:text-left md:text-right">
                <div className="content-label-name">상세 주소</div>
                <div className="content-label-desc" />
              </div>
              <div className="col-span-10">
                <div className="grow pr-3">
                  <TextBox
                    value={hCostResult.conAddressDetail}
                    onValueChanged={onValueChangeCounselHandler((draft, value) => {
                      draft.conAddressDetail = value;
                    })}
                  />
                </div>
                <div className="content-value-desc" />
              </div>
            </div>
            <div className="content-value-desc" />
          </div>
        </div>

        <div className="content-field row">
          <div className="content-field-label">
            <div className="content-label-name">토지 소유</div>
            <div className="content-label-desc" />
          </div>
          <div className="content-field-value full">
            <div className="flex flex-wrap gap-y-2 gap-x-3 mb-2">
              {HCostData.conLandOwnershipType_V2.items.map((v: DataItem) => (
                <Radio
                  labelClassName="!text-sm"
                  key={v.value}
                  label={v.text}
                  value={v.value}
                  name={"conLandOwnershipType"}
                  onClick={handleRadioChecked((draft, value) => {
                    if (draft.conLandOwnershipType === value) {
                      draft.conLandOwnershipType = null;
                    } else {
                      draft.conLandOwnershipType = value;
                    }
                  })}
                  checked={hCostResult.conLandOwnershipType === v.value}
                  readOnly={true}
                ></Radio>
              ))}
            </div>

            <div className="content-value-desc" />
          </div>
        </div>

        <div className="content-field row">
          <div className="content-field-label">
            <div className="content-label-name">정부지원사업</div>
            <div className="content-label-desc" />
          </div>
          <div className="content-field-value full">
            <div className="grid sm:grid-cols-12 gap-4">
              <div className="col-span-10">
                <div className="grow pr-3 flex gap-4 flex-wrap">
                  {HCostData.conSupportStatus.items.map((v: DataItem) => (
                    <Radio
                      labelClassName="!text-sm"
                      key={v.value}
                      label={v.text}
                      value={v.value}
                      name={"conSupportStatus"}
                      onClick={handleRadioChecked((draft, value) => {
                        if (draft.conSupportStatus === value) {
                          draft.conSupportStatus = null;
                        } else {
                          draft.conSupportStatus = value;
                        }
                      })}
                      checked={hCostResult.conSupportStatus === v.value}
                      readOnly={true}
                    ></Radio>
                  ))}
                </div>
                <div className="content-value-desc" />
              </div>
            </div>
            <div className="content-value-desc" />
          </div>

          <div className="col-span-3 sm:text-right md:text-right">
            <div className="content-label-name">지원 형태</div>
            <div className="content-label-desc" />
          </div>
          <div className="col-span-9">
            <div className="grow pr-3 flex gap-4 flex-wrap">
              {HCostData.conSupportType_V3.items.map((v: any) => (
                <Radio
                  labelClassName="!text-sm"
                  key={v.value}
                  label={v.text}
                  value={v.value}
                  name={"conSupportType"}
                  onClick={handleRadioChecked((draft, value) => {
                    if (draft.conSupportType === value) {
                      draft.conSupportType = null;
                    } else {
                      draft.conSupportType = value;
                    }
                  })}
                  checked={hCostResult.conSupportType === v.value}
                  readOnly={true}
                ></Radio>
              ))}
            </div>
            <div className="content-value-desc" />
          </div>

          <div className="col-span-3 sm:text-right md:text-right">
            <div className="content-label-name">지원 사업명</div>
            <div className="content-label-desc" />
          </div>
          <div className="col-span-9">
            <div className="grow pr-3">
              <TextBox
                value={hCostResult.conSupportName}
                showClearButton
                onValueChanged={onValueChangeCounselHandler((draft, value) => {
                  draft.conSupportName = value;
                })}
              />
            </div>
            <div className="content-value-desc" />
          </div>
        </div>

        <div className="content-field row">
          <div className="content-field-label col-12 col-sm-4 col-md-3 col-lg-2">
            <div className="content-label-name">최대 예산</div>
            <div className="content-label-desc" />
          </div>
          <div className="content-field-value full">
            <NumberBox
              format={"#,##0.#" + HCostData.conBudget.data.unit}
              max={HCostData.conBudget.data.max}
              min={HCostData.conBudget.data.min}
              showSpinButtons={true}
              step={HCostData.conBudget.data.step}
              value={hCostResult.conBudget}
              onValueChanged={onValueChangeCounselHandler((draft, value) => {
                draft.conBudget = value;
              })}
            />
            <div className="justify-start xl:justify-between flex flex-wrap">
              <Button
                className="con-budget-button mt-1 mr-1"
                text="+1백만원"
                onClick={event => conBudgetClick(event, 100)}
              />
              <Button
                className="con-budget-button mt-1 mr-1"
                text="+5백만원"
                onClick={event => conBudgetClick(event, 500)}
              />
              <Button
                className="con-budget-button mt-1 mr-1"
                text="+1천만원"
                onClick={event => conBudgetClick(event, 1000)}
              />
              <Button
                className="con-budget-button mt-1 mr-1"
                text="+5천만원"
                onClick={event => conBudgetClick(event, 5000)}
              />
              <Button
                className="con-budget-button mt-1 mr-1"
                text="+1억원"
                onClick={event => conBudgetClick(event, 10000)}
              />
              <Button
                className="con-budget-button mt-1"
                text="+5억원"
                onClick={event => conBudgetClick(event, 50000)}
              />
            </div>
            <div className="content-value-desc" />
          </div>
        </div>
      </div>
    </>
  );
};

export default CounselConstructionInfo;
